/**
 * Legal Reference Detection and Linking
 *
 * Extensible system for detecting legal article references in text
 * and converting them to clickable links.
 */

// ============================================================================
// Types
// ============================================================================

export interface LegalRefPattern {
  /** Unique identifier for this pattern type */
  type: string;
  /** Regex pattern to match references (must have a capture group for the article number) */
  pattern: RegExp;
  /** Function to normalize the matched reference to a canonical form for API lookup */
  normalize: (match: string) => string;
  /** Function to fetch the URL for a normalized reference */
  fetchUrl: (normalized: string) => Promise<string | null>;
  /** Base URL template (for display purposes) */
  baseUrl?: string;
}

export interface ResolvedRef {
  original: string;
  normalized: string;
  url: string;
  type: string;
}

// ============================================================================
// Cache
// ============================================================================

const urlCache = new Map<string, string | null>();

// ============================================================================
// LEGI Article Pattern (Code du travail)
// ============================================================================

/**
 * Matches French Labor Code article references:
 * - L1234-1, L1234-1-1, L1234-1-1-1
 * - L. 1234-1, L. 1234-1-1
 * - R1234-1, R. 1234-1 (regulatory)
 * - D1234-1, D. 1234-1 (decree)
 */
const LEGI_ARTICLE_PATTERN = /\b([LRD])\.?\s?(\d{1,4}(?:-\d+)+)\b/gi;

function normalizeLegiArticle(match: string): string {
  // Extract the letter and numbers, normalize to "L1234-1" format
  const normalized = match
    .replace(/\.\s?/g, "") // Remove period and optional space
    .replace(/\s+/g, "") // Remove any remaining spaces
    .toUpperCase();
  return normalized;
}

async function fetchLegiUrl(normalized: string): Promise<string | null> {
  const cacheKey = `legi:${normalized}`;

  if (urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey)!;
  }

  try {
    const response = await fetch(`/api/legi/article/num/${encodeURIComponent(normalized)}`);

    if (!response.ok) {
      urlCache.set(cacheKey, null);
      return null;
    }

    const data = await response.json();
    const url = data.url || null;
    urlCache.set(cacheKey, url);
    return url;
  } catch (error) {
    console.warn(`Failed to fetch URL for ${normalized}:`, error);
    urlCache.set(cacheKey, null);
    return null;
  }
}

export const legiArticlePattern: LegalRefPattern = {
  type: "legi",
  pattern: LEGI_ARTICLE_PATTERN,
  normalize: normalizeLegiArticle,
  fetchUrl: fetchLegiUrl,
  baseUrl: "https://www.legifrance.gouv.fr/codes/article_lc/",
};

// ============================================================================
// Pattern Registry
// ============================================================================

const patterns: LegalRefPattern[] = [
  legiArticlePattern,
  // Add more patterns here as needed:
  // - KALI article patterns (IDCC references)
  // - European law references
  // - Case law references (Cour de cassation, etc.)
];

/**
 * Register a new legal reference pattern
 */
export function registerPattern(pattern: LegalRefPattern): void {
  patterns.push(pattern);
}

// ============================================================================
// Reference Detection
// ============================================================================

interface DetectedRef {
  original: string;
  normalized: string;
  type: string;
  index: number;
}

/**
 * Detect all legal references in text
 */
export function detectRefs(text: string): DetectedRef[] {
  const refs: DetectedRef[] = [];
  const seen = new Set<string>();

  for (const pattern of patterns) {
    // Reset regex lastIndex for global patterns
    pattern.pattern.lastIndex = 0;

    let match;
    while ((match = pattern.pattern.exec(text)) !== null) {
      const original = match[0];
      const normalized = pattern.normalize(original);
      const key = `${pattern.type}:${normalized}`;

      if (!seen.has(key)) {
        seen.add(key);
        refs.push({
          original,
          normalized,
          type: pattern.type,
          index: match.index,
        });
      }
    }
  }

  return refs;
}

/**
 * Resolve URLs for detected references (with batching and caching)
 */
export async function resolveRefs(refs: DetectedRef[]): Promise<Map<string, ResolvedRef>> {
  const resolved = new Map<string, ResolvedRef>();

  // Group by type for potential batch optimization
  const byType = new Map<string, DetectedRef[]>();
  for (const ref of refs) {
    const list = byType.get(ref.type) || [];
    list.push(ref);
    byType.set(ref.type, list);
  }

  // Resolve each type
  for (const [type, typeRefs] of byType) {
    const pattern = patterns.find((p) => p.type === type);
    if (!pattern) continue;

    // Fetch URLs in parallel
    const results = await Promise.all(
      typeRefs.map(async (ref) => {
        const url = await pattern.fetchUrl(ref.normalized);
        return { ref, url };
      }),
    );

    for (const { ref, url } of results) {
      if (url) {
        resolved.set(ref.normalized, {
          original: ref.original,
          normalized: ref.normalized,
          url,
          type: ref.type,
        });
      }
    }
  }

  return resolved;
}

/**
 * Pattern to match existing markdown links containing article references
 * Captures: [Article L1234-13](url) or [L1234-13](url)
 */
const EXISTING_LINK_PATTERN = /\[(?:Article\s+)?([LRD])\.?\s?(\d{1,4}(?:-\d+)+)\]\([^)]+\)/gi;

/**
 * Process text and replace legal references with markdown links
 */
export async function linkifyLegalRefs(text: string): Promise<string> {
  const refs = detectRefs(text);

  if (refs.length === 0) {
    return text;
  }

  const resolved = await resolveRefs(refs);

  if (resolved.size === 0) {
    return text;
  }

  // Build replacement maps
  // 1. linkReplacements: existing markdown links -> new markdown links
  // 2. textReplacements: plain text -> new markdown links
  const linkReplacements = new Map<string, string>();
  const textReplacements = new Map<string, string>();

  // First pass: Find existing markdown links with article references
  EXISTING_LINK_PATTERN.lastIndex = 0;
  let linkMatch;
  while ((linkMatch = EXISTING_LINK_PATTERN.exec(text)) !== null) {
    const fullLink = linkMatch[0];
    const letter = linkMatch[1];
    const numbers = linkMatch[2];
    if (!letter || !numbers) continue;
    // Normalize the article reference (letter + numbers)
    const normalized = `${letter.toUpperCase()}${numbers}`;
    const resolved_ref = resolved.get(normalized);

    if (resolved_ref && !linkReplacements.has(fullLink)) {
      // Replace with normalized article reference and new URL
      linkReplacements.set(fullLink, `[${normalized}](${resolved_ref.url})`);
    }
  }

  // Second pass: Find plain article references (not inside links)
  for (const pattern of patterns) {
    pattern.pattern.lastIndex = 0;

    let match;
    while ((match = pattern.pattern.exec(text)) !== null) {
      const original = match[0];
      const normalized = pattern.normalize(original);
      const resolved_ref = resolved.get(normalized);

      if (resolved_ref && !textReplacements.has(original)) {
        // Only linkify if not already a link (check for surrounding brackets/parens)
        const before = text.slice(Math.max(0, match.index - 2), match.index);
        const after = text.slice(match.index + original.length, match.index + original.length + 2);

        // Skip if already in a markdown link [text](url) or already linked
        if (before.includes("[") || after.includes("]")) {
          continue;
        }

        textReplacements.set(original, `[${original}](${resolved_ref.url})`);
      }
    }
  }

  // Apply replacements (link replacements first, then text replacements)
  // Sort by length descending to handle overlapping matches
  let result = text;

  // Apply link replacements first (they're more specific)
  const sortedLinkReplacements = [...linkReplacements.entries()].sort(
    (a, b) => b[0].length - a[0].length,
  );
  for (const [original, replacement] of sortedLinkReplacements) {
    result = result.split(original).join(replacement);
  }

  // Then apply text replacements
  const sortedTextReplacements = [...textReplacements.entries()].sort(
    (a, b) => b[0].length - a[0].length,
  );
  for (const [original, replacement] of sortedTextReplacements) {
    result = replaceUnlinked(result, original, replacement);
  }

  return result;
}

/**
 * Replace text only if not already inside a markdown link
 */
function replaceUnlinked(text: string, search: string, replacement: string): string {
  const result: string[] = [];
  let lastIndex = 0;
  let index = text.indexOf(search, lastIndex);

  while (index !== -1) {
    // Check if this occurrence is inside a markdown link
    const before = text.slice(Math.max(0, index - 100), index);
    const after = text.slice(
      index + search.length,
      Math.min(text.length, index + search.length + 100),
    );

    // Simple heuristic: if there's an unclosed [ before and a ]( after, skip
    const lastOpenBracket = before.lastIndexOf("[");
    const lastCloseBracket = before.lastIndexOf("]");
    const isInsideLink = lastOpenBracket > lastCloseBracket && after.includes("](");

    // Also skip if immediately followed by ]( (it's the link text)
    const isLinkText = after.startsWith("](") || after.startsWith("]");

    if (isInsideLink || isLinkText) {
      result.push(text.slice(lastIndex, index + search.length));
    } else {
      result.push(text.slice(lastIndex, index));
      result.push(replacement);
    }

    lastIndex = index + search.length;
    index = text.indexOf(search, lastIndex);
  }

  result.push(text.slice(lastIndex));
  return result.join("");
}

// ============================================================================
// Preload Cache (for known references)
// ============================================================================

/**
 * Preload cache with known URLs (useful for batch operations)
 */
export function preloadCache(
  entries: Array<{ normalized: string; type: string; url: string }>,
): void {
  for (const entry of entries) {
    const cacheKey = `${entry.type}:${entry.normalized}`;
    urlCache.set(cacheKey, entry.url);
  }
}

/**
 * Clear the URL cache
 */
export function clearCache(): void {
  urlCache.clear();
}

/**
 * Get cache statistics
 */
export function getCacheStats(): { size: number; hits: number } {
  return {
    size: urlCache.size,
    hits: urlCache.size, // Simplified - actual hit tracking would need more state
  };
}
